/* eslint-disable @typescript-eslint/naming-convention */
import { z } from 'zod';
import {
    LoanApplicantIdDocumentType,
    ResidenceOwnership,
} from '@servicetitan/financing-api-client';
import { isValidDate } from '@servicetitan/financing-utils-shared';
import {
    resolveFieldOrder,
    getPersonalInfoBaseFields,
    resolveResidenceOwnershipValueSet,
    type ProviderConfig,
    type ProviderFieldOverride,
} from '@servicetitan/financing-workflow-shared';
import { PERSONAL_INFO_FIELD_REGISTRY } from '../personal-information/fieldRegistry';
import set from 'lodash/set';
import { spouseInfoSchema, validateSimpleMarriageFields } from '../spouseInfo';
import { createAddressSchema, type AddressValidationOptions } from '../common/addressSchema';

const noDigitsOrSpecialCharsRegex = /^[^!@#$%^&*(),.?":{}|<>1234567890]+$/;

const baseIdType = z.object({
    applicantIdDocumentType: z.nativeEnum(LoanApplicantIdDocumentType),
    documentId: z.string({ required_error: 'Enter an ID number' }).min(1, 'Enter an ID number'),
    issueDate: z
        .string({ required_error: 'Enter an issue date' })
        .regex(/^\d{2}\/\d{2}\/\d{4}$/, { message: 'Does not match format: mm/dd/yyyy' })
        .refine(
            date => {
                return isValidDate(date);
            },
            { message: 'Enter a valid date' },
        )
        .refine(
            date => {
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                return new Date(date) <= today;
            },
            { message: 'Issue date must be in the past' },
        ),
    expirationDate: z
        .string({ required_error: 'Enter an expiration date' })
        .regex(/^\d{2}\/\d{2}\/\d{4}$/, { message: 'Does not match format: mm/dd/yyyy' })
        .refine(
            date => {
                return isValidDate(date);
            },
            { message: 'Enter a valid date' },
        )
        .refine(
            date => {
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                return new Date(date) >= today;
            },
            { message: 'Expiry date must be in the future' },
        ),
});

const driverLicense = baseIdType.merge(
    z.object({
        applicantIdDocumentType: z.literal(LoanApplicantIdDocumentType.DriversLicense),
        state: z
            .string({
                required_error: 'Select a state',
                invalid_type_error: 'Select a state',
            })
            .nonempty({
                message: 'Select a state',
            }),
    }),
);

const stateId = baseIdType.merge(
    z.object({
        applicantIdDocumentType: z.literal(LoanApplicantIdDocumentType.StateId),
        state: z.string({ required_error: 'Select a state', invalid_type_error: 'Select a state' }),
    }),
);

const greenCard = baseIdType.merge(
    z.object({
        applicantIdDocumentType: z.literal(LoanApplicantIdDocumentType.UsaGreenCard),
        country: z
            .string({ required_error: 'Enter a country' })
            .nonempty({ message: 'Enter a country' })
            .regex(noDigitsOrSpecialCharsRegex, {
                message: 'Cannot contain digits or special characters',
            }),
    }),
);

const passport = baseIdType.merge(
    z.object({
        applicantIdDocumentType: z.literal(LoanApplicantIdDocumentType.Passport),
        country: z
            .string({ required_error: 'Enter a country' })
            .nonempty({ message: 'Enter a country' })
            .regex(noDigitsOrSpecialCharsRegex, {
                message: 'Cannot contain digits or special characters',
            }),
    }),
);

const militaryId = baseIdType.merge(
    z.object({
        applicantIdDocumentType: z.literal(LoanApplicantIdDocumentType.MilitaryId),
        country: z
            .string({ required_error: 'Enter a country' })
            .nonempty({ message: 'Enter a country' })
            .regex(noDigitsOrSpecialCharsRegex, {
                message: 'Cannot contain digits or special characters',
            }),
        issuerName: z
            .string({ required_error: 'Enter an issuer name' })
            .nonempty({ message: 'Enter an issuer name' })
            .regex(noDigitsOrSpecialCharsRegex, {
                message: 'Cannot contain digits or special characters',
            }),
    }),
);

const visa = baseIdType.merge(
    z.object({
        applicantIdDocumentType: z.literal(LoanApplicantIdDocumentType.Visa),
        governmentBranch: z.string({ required_error: 'Enter a government branch' }),
    }),
);

export const createJobDetailsSchema = (
    statesWithSpouseInformation: string[] = [],
    requireMortgageDetails: boolean = false,
    requireIndustryType: boolean = true,
    useImprovementTypeInJobDetails: boolean = false,
    hasDetailedMarriageFields: boolean = false,
    addressValidation?: AddressValidationOptions,
    secondLookProviderConfig?: Pick<ProviderConfig, 'fieldValueSets'>,
) => {
    // When using improvement type instead of industry type, make industry type optional
    // Otherwise, use requireIndustryType to determine if it's required
    const industryTypeField = useImprovementTypeInJobDetails
        ? z.string().optional()
        : requireIndustryType
          ? z
                .string({ required_error: 'Select an industry type' })
                .min(1, 'Select an industry type')
          : z.string().optional();
    const isManufacturedHomeField = requireMortgageDetails
        ? z.boolean({ required_error: 'Select Yes or No' })
        : z.boolean().optional();

    const jobDetails = z
        .object({
            address: createAddressSchema(addressValidation),
            industryType: industryTypeField,
            isManufacturedHome: isManufacturedHomeField,
            residenceType: z.enum(
                resolveResidenceOwnershipValueSet(secondLookProviderConfig).allowed as [
                    ResidenceOwnership,
                    ...ResidenceOwnership[],
                ],
                { required_error: 'Select a residence type' },
            ),
            mortgageMonthlyPayment: z.union([z.string(), z.number()]).optional().nullable(),
            mortgageEstimatedBalance: z.union([z.string(), z.number()]).optional().nullable(),
            spouseInfo: spouseInfoSchema.optional(),
            isMarried: z.boolean().optional(),
            spouseFirstName: z.string().optional(),
            spouseLastName: z.string().optional(),
            spouseContact: z.string().optional(),
            // Mailing/home-address override (rendered only when the provider sets
            // allowMailingAddressOverride). Optional here; required-when-checked is
            // enforced in the superRefine below so it stays inert for other providers.
            mailingAddressDiffersFromJobAddress: z.boolean().optional(),
            mailingAddress: z
                .object({ address: createAddressSchema(addressValidation) })
                .optional(),
        })
        .superRefine((data, ctx) => {
            if (requireMortgageDetails && String(data.residenceType) === 'ownWithMortgage') {
                const normalize = (v: unknown) => {
                    if (v === null || v === undefined) {
                        return NaN;
                    }
                    if (typeof v === 'number') {
                        return v;
                    }
                    // eslint-disable-next-line @typescript-eslint/no-base-to-string
                    const s = String(v).replace(/[^0-9.-]/g, '');
                    return Number(s);
                };

                const pay = normalize((data as any).mortgageMonthlyPayment);
                const bal = normalize((data as any).mortgageEstimatedBalance);

                if (!Number.isFinite(pay) || pay <= 0) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: 'Enter your monthly mortgage payment',
                        path: ['mortgageMonthlyPayment'],
                    });
                }
                if (!Number.isFinite(bal) || bal <= 0) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: 'Enter your estimated mortgage balance',
                        path: ['mortgageEstimatedBalance'],
                    });
                }
            }

            if (
                !hasDetailedMarriageFields &&
                data.address.state &&
                statesWithSpouseInformation.includes(data.address.state)
            ) {
                validateSimpleMarriageFields(data, ctx);
            }

            // The per-field address rules only fire once the object exists; require it
            // outright when the applicant says the mailing address differs from the job.
            if (data.mailingAddressDiffersFromJobAddress && !data.mailingAddress?.address) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: 'Enter a mailing address',
                    path: ['mailingAddress', 'address', 'street'],
                });
            }
        })
        .refine(
            data => {
                if (
                    data.address.state &&
                    statesWithSpouseInformation.includes(data.address.state)
                ) {
                    if (hasDetailedMarriageFields) {
                        return data.spouseInfo !== undefined;
                    }
                    return data.isMarried !== undefined;
                }
                return true;
            },
            () => ({
                message: hasDetailedMarriageFields
                    ? 'Spouse information is required for this state'
                    : 'Select a marital status',
                path: hasDetailedMarriageFields ? ['spouseInfo'] : ['isMarried'],
            }),
        );

    return jobDetails;
};

export const jobDetailsSchema = createJobDetailsSchema();

export const createApplicantSchema = (
    config: {
        additionalApplicantFields?: string[];
        providerCategory?: string;
        statesWithSpouseInformation?: string[];
        useAnnualIncome?: boolean;
        personalInfoFieldOverrides?: ProviderFieldOverride;
        turnsOverrides?: ProviderFieldOverride;
        hasDetailedMarriageFields?: boolean;
        addressValidation?: AddressValidationOptions;
        secondLookProviderConfig?: Pick<ProviderConfig, 'fieldValueSets'>;
    } = {},
) => {
    const {
        additionalApplicantFields = [],
        providerCategory,
        statesWithSpouseInformation = [],
        useAnnualIncome = false,
        personalInfoFieldOverrides,
        hasDetailedMarriageFields = false,
        addressValidation,
        secondLookProviderConfig,
    } = config;

    // Get field order from registry (for backward compatibility, also check additionalApplicantFields)
    const providerConfig: ProviderConfig = {
        personalInfoFieldOverrides,
        providerCategory: providerCategory as 'first-look' | 'second-look',
        useAnnualIncome,
    } as ProviderConfig;

    // Get Turns overrides if provided (when Turns co-application is enabled)
    // Skip requestedLoanAmount - that's handled elsewhere
    const turnsOverrides = config.turnsOverrides
        ? {
              additions: config.turnsOverrides.additions?.filter(
                  (f: string) => f !== 'requestedLoanAmount',
              ),
          }
        : undefined;

    const fieldOrder = resolveFieldOrder(
        getPersonalInfoBaseFields(useAnnualIncome),
        personalInfoFieldOverrides,
        turnsOverrides,
    );

    // Build base schema from field registry
    const baseSchema: Record<string, z.ZodTypeAny> = {};

    // Fields that belong to nested employment object
    const employmentFields = [
        'employerName',
        'grossMonthlyIncome',
        'incomeType',
        'employmentStatus',
    ];

    // Build employment schema separately
    const employmentSchema: Record<string, z.ZodTypeAny> = {};

    // Process fields in order
    for (const fieldKey of fieldOrder) {
        const field = PERSONAL_INFO_FIELD_REGISTRY[fieldKey];
        if (!field) {
            continue;
        }

        const fieldSchema =
            typeof field.schema === 'function' ? field.schema(providerConfig) : field.schema;

        // Check if this field maps to employment.* (either by fieldKey or schemaKey)
        const schemaKey = field.schemaKey ?? fieldKey;
        const isEmploymentField =
            employmentFields.includes(fieldKey) || schemaKey.startsWith('employment.');

        if (isEmploymentField) {
            // Extract the employment field name from schemaKey (e.g., 'employment.grossMonthlyIncome' -> 'grossMonthlyIncome')
            const employmentFieldName = schemaKey.startsWith('employment.')
                ? schemaKey.replace('employment.', '')
                : fieldKey;

            // Map field keys to employment schema keys
            if (employmentFieldName === 'status' || fieldKey === 'employmentStatus') {
                employmentSchema.status = fieldSchema;
            } else if (employmentFieldName === 'employerName' || fieldKey === 'employerName') {
                employmentSchema.employerName = fieldSchema;
            } else if (
                employmentFieldName === 'grossMonthlyIncome' ||
                fieldKey === 'grossMonthlyIncome'
            ) {
                employmentSchema.grossMonthlyIncome = fieldSchema;
            } else if (employmentFieldName === 'incomeType' || fieldKey === 'incomeType') {
                employmentSchema.incomeType = fieldSchema;
            } else {
                // For any other employment.* fields, use the field name directly
                employmentSchema[employmentFieldName] = fieldSchema;
            }
        } else {
            // Top-level fields
            // Using lodash set to allow for nested fields
            set(baseSchema, schemaKey, fieldSchema);
        }
    }

    // Use .default({}) so that if no employment fields are collected (e.g., when using annualIncome
    // at top level), the missing employment object doesn't cause a validation error
    baseSchema.employment = z.object(employmentSchema).passthrough().default({});

    // Employment schema is now built from field registry above

    if (additionalApplicantFields.includes('jobDetails')) {
        baseSchema.jobDetails = createJobDetailsSchema(
            statesWithSpouseInformation,
            additionalApplicantFields.includes('mortgageDetails'),
            true,
            false,
            hasDetailedMarriageFields,
            addressValidation,
            secondLookProviderConfig,
        );
    }

    baseSchema.phoneConsent = z.boolean().optional();

    // Add applicantId validation based on identification requirement
    if (additionalApplicantFields.includes('identification')) {
        baseSchema.applicantId = z.discriminatedUnion('applicantIdDocumentType', [
            driverLicense,
            stateId,
            passport,
            militaryId,
            visa,
            greenCard,
        ]);
    } else {
        baseSchema.applicantId = z
            .discriminatedUnion('applicantIdDocumentType', [
                driverLicense,
                stateId,
                passport,
                militaryId,
                visa,
                greenCard,
            ])
            .optional();
    }

    const schema = z.object(baseSchema);

    // Conditionally require phoneConsent when it exists in the form data
    return schema.superRefine((data, ctx) => {
        if ('phoneConsent' in data && data.phoneConsent !== true) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Must check the Phone Number disclosure',
                path: ['phoneConsent'],
            });
        }
    });
};

export const createSchema = (
    config: {
        additionalApplicantFields?: string[];
        providerCategory?: string;
        statesWithSpouseInformation?: string[];
        useAnnualIncome?: boolean;
        personalInfoFieldOverrides?: ProviderFieldOverride;
        turnsOverrides?: ProviderFieldOverride;
        addressValidation?: AddressValidationOptions;
        secondLookProviderConfig?: Pick<ProviderConfig, 'fieldValueSets'>;
    } = {},
    turnsOverrides?: ProviderFieldOverride,
) =>
    z.object({
        primaryApplicant: createApplicantSchema({
            additionalApplicantFields:
                'additionalApplicantFields' in config
                    ? config.additionalApplicantFields
                    : undefined,
            providerCategory: config.providerCategory,
            statesWithSpouseInformation: config.statesWithSpouseInformation,
            useAnnualIncome: config.useAnnualIncome,
            personalInfoFieldOverrides:
                'personalInfoFieldOverrides' in config
                    ? config.personalInfoFieldOverrides
                    : undefined,
            turnsOverrides,
            addressValidation: config.addressValidation,
            secondLookProviderConfig: config.secondLookProviderConfig,
        }),
    });

// Default schema for backward compatibility
export const applicantSchema = createApplicantSchema();
export const schema = createSchema();

export type BaseApplicantData = z.infer<typeof applicantSchema>;
export type PrimaryApplicantData = z.infer<typeof schema>;
